import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { useOptionalActiveEdit } from './ContentEditModeProvider';
import { EquationEditor } from './EquationEditor';
import { ImageUnitEditor, type ImageWait } from './ImageUnitEditor';
import { IframeUnitEditor } from './IframeUnitEditor';
import { TableUnitEditor } from './TableUnitEditor';
import { TextUnitEditor } from './TextUnitEditor';
import type { EditableContentUnit } from './editorTypes';
import { VideoUnitEditor } from './VideoUnitEditor';
import styles from './ContentUnitEditMode.module.css';

interface ContentUnitEditModeProps {
  index: number;
  onCancel: () => void;
  onDraftChange?: (unit: EditableContentUnit) => void;
  onDirtyChange: () => void;
  onSave: (unit: EditableContentUnit) => void;
  unit: EditableContentUnit;
}

const IMAGE_SIZE_OPTIONS: Array<{
  label: string;
  value: NonNullable<EditableContentUnit['imageSize']>;
}> = [
  { label: 'Small', value: 'SMALL' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Large', value: 'LARGE' },
];

function isMobileViewport() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 47.99rem)').matches
  );
}

/** Why Save unit waits, while a new image is not ready to be saved. */
const IMAGE_WAIT_REASONS: Record<ImageWait, string> = {
  copy: 'Wait while the image is prepared.',
  upload: 'Wait for the image to finish uploading.',
};

function getUnitTypeLabel(editorType: EditableContentUnit['editorType']) {
  switch (editorType) {
    case 'EQUATION':
      return 'Equation';
    case 'IFRAME':
      return 'Iframe';
    case 'IMAGE':
      return 'Image';
    case 'TABLE':
      return 'Table';
    case 'VIDEO':
      return 'Video';
    default:
      return 'Text';
  }
}

export function ContentUnitEditMode({
  index,
  onCancel,
  onDraftChange,
  onDirtyChange,
  onSave,
  unit,
}: ContentUnitEditModeProps) {
  const advancedOptionsId = useId();
  const saveReasonId = useId();
  const onDraftChangeRef = useRef(onDraftChange);
  const panelRef = useRef<HTMLElement | null>(null);
  const [draft, setDraft] = useState(unit);
  const [imageWait, setImageWait] = useState<ImageWait | null>(null);
  const saveReasonRef = useRef<HTMLParagraphElement | null>(null);
  // Outside the chapter page, as in the unrouted editor page, there is none.
  const activeEdit = useOptionalActiveEdit();
  const setUnitWait = activeEdit?.setUnitWait;
  const leaveRefusals = activeEdit?.leaveRefusals ?? 0;
  const seenLeaveRefusalsRef = useRef(leaveRefusals);

  // The page refuses to leave this unit, by any of its controls, while its
  // image waits: it learns what the image waits for here.
  useEffect(() => {
    if (!setUnitWait) {
      return undefined;
    }

    setUnitWait(unit.id, imageWait);

    return () => {
      setUnitWait(unit.id, null);
    };
  }, [imageWait, setUnitWait, unit.id]);

  // Refused, the instructor is taken to why the card stays open. A menu whose
  // item was refused hands focus back to its button as it closes, after any
  // closing animation: focus taken from the reason so, before the instructor
  // next presses a key or the pointer, comes back to it, once.
  useEffect(() => {
    if (leaveRefusals === seenLeaveRefusalsRef.current) {
      return undefined;
    }

    seenLeaveRefusalsRef.current = leaveRefusals;
    const reason = saveReasonRef.current;

    if (!reason) {
      return undefined;
    }

    reason.focus();

    let watch = 0;
    const takeBack = (event: FocusEvent) => {
      if (event.target !== reason) {
        stop();
        reason.focus();
      }
    };
    const stop = () => {
      window.clearTimeout(watch);
      document.removeEventListener('focusin', takeBack, true);
      document.removeEventListener('keydown', stop, true);
      document.removeEventListener('pointerdown', stop, true);
    };

    document.addEventListener('focusin', takeBack, true);
    document.addEventListener('keydown', stop, true);
    document.addEventListener('pointerdown', stop, true);
    watch = window.setTimeout(stop, 1000);

    return stop;
  }, [leaveRefusals]);

  const hasAdvancedCaption = draft.editorType === 'VIDEO';
  const defaultAdvancedExpanded =
    (hasAdvancedCaption && Boolean(draft.caption?.trim())) ||
    draft.collapseByDefault ||
    draft.disableAutoNumbering;
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(
    defaultAdvancedExpanded,
  );

  useEffect(() => {
    onDraftChangeRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    setDraft(unit);
    onDraftChangeRef.current?.(unit);
    setIsAdvancedExpanded(
      (unit.editorType === 'VIDEO' && Boolean(unit.caption?.trim())) ||
        unit.collapseByDefault ||
        unit.disableAutoNumbering,
    );
  }, [unit]);

  useEffect(() => {
    const focusable = panelRef.current?.querySelector<HTMLElement>(
      'input, textarea, select, button',
    );

    focusable?.focus();
  }, []);

  const updateDraft = (updates: Partial<EditableContentUnit>) => {
    setDraft((currentDraft) => {
      const nextDraft = {
        ...currentDraft,
        ...updates,
        validationError:
          currentDraft.editorType === 'IMAGE' &&
          ('imageUrl' in updates || 'dynamicImageUrl' in updates)
            ? undefined
            : currentDraft.validationError,
      };

      onDraftChangeRef.current?.(nextDraft);
      return nextDraft;
    });
    onDirtyChange();
  };

  const scrollPanelIntoView = () => {
    if (!isMobileViewport()) {
      return;
    }

    const schedule =
      window.requestAnimationFrame ??
      ((callback: FrameRequestCallback) => window.setTimeout(callback, 0));

    schedule(() => {
      panelRef.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    });
  };

  const unitTypeLabel = getUnitTypeLabel(draft.editorType);

  useEffect(() => {
    scrollPanelIntoView();
  }, []);

  useEffect(() => {
    const panel = panelRef.current;

    if (!panel) {
      return undefined;
    }

    panel.addEventListener('focusin', scrollPanelIntoView);

    return () => {
      panel.removeEventListener('focusin', scrollPanelIntoView);
    };
  }, []);

  return (
    <article
      aria-label={`Editing content item ${index + 1}`}
      className={styles.card}
      data-invalid={draft.validationError ? 'true' : 'false'}
      ref={panelRef}
    >
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Editing content item {index + 1}</p>
          <h3 className={styles.title}>{unitTypeLabel}</h3>
        </div>
        <div className={styles.headerSide}>
          <div className={styles.headerActions}>
            <button className={styles.cancelButton} onClick={onCancel} type="button">
              Cancel
            </button>
            <button
              aria-describedby={imageWait ? saveReasonId : undefined}
              className={styles.saveButton}
              disabled={imageWait !== null}
              onClick={() => {
                onSave({
                  ...draft,
                  isNew: false,
                });
              }}
              type="button"
            >
              Save unit
            </button>
          </div>
          {draft.editorType === 'IMAGE' ? (
            // There from the start, so that a screen reader hears each reason.
            <p
              className={styles.saveReason}
              id={saveReasonId}
              ref={saveReasonRef}
              role="status"
              tabIndex={-1}
            >
              {imageWait ? IMAGE_WAIT_REASONS[imageWait] : ''}
            </p>
          ) : null}
        </div>
      </header>

      <div className={styles.sharedFields}>
        {draft.editorType === 'IMAGE' || draft.editorType === 'TABLE' ? (
          <div className={styles.imagePrimaryRow}>
            <label className={[styles.field, styles.captionField].join(' ')}>
              <span className={styles.label}>Caption</span>
              <input
                className={styles.input}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  updateDraft({ caption: event.target.value });
                }}
                type="text"
                value={draft.caption ?? ''}
              />
            </label>

            {draft.editorType === 'IMAGE' ? (
              <label className={[styles.field, styles.sizeField].join(' ')}>
                <span className={styles.label}>Size</span>
                <select
                  className={styles.select}
                  onChange={(event) => {
                    updateDraft({
                      imageSize: event.target.value as NonNullable<
                        EditableContentUnit['imageSize']
                      >,
                    });
                  }}
                  value={draft.imageSize ?? 'MEDIUM'}
                >
                  {IMAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className={[styles.field, styles.captionField].join(' ')}>
                <span className={styles.label}>Heading</span>
                <input
                  className={styles.input}
                  onChange={(event) => {
                    updateDraft({ heading: event.target.value });
                  }}
                  type="text"
                  value={draft.heading ?? ''}
                />
              </label>
            )}
          </div>
        ) : (
          <label className={styles.field}>
            <span className={styles.label}>Heading</span>
            <input
              className={styles.input}
              onChange={(event) => {
                updateDraft({ heading: event.target.value });
              }}
              type="text"
              value={draft.heading ?? ''}
            />
          </label>
        )}
      </div>

      <div className={styles.advancedOptions}>
        <button
          aria-controls={advancedOptionsId}
          aria-expanded={isAdvancedExpanded}
          className={styles.advancedToggle}
          onClick={() => {
            setIsAdvancedExpanded((currentValue) => !currentValue);
          }}
          type="button"
        >
          <span>Advanced options</span>
          {isAdvancedExpanded ? (
            <ChevronUp aria-hidden="true" size={18} strokeWidth={2} />
          ) : (
            <ChevronDown aria-hidden="true" size={18} strokeWidth={2} />
          )}
        </button>

        {isAdvancedExpanded ? (
          <div className={styles.advancedContent} id={advancedOptionsId}>
            {hasAdvancedCaption ? (
              <label className={styles.field}>
                <span className={styles.label}>Caption</span>
                <input
                  className={styles.input}
                  onChange={(event) => {
                    updateDraft({ caption: event.target.value });
                  }}
                  type="text"
                  value={draft.caption ?? ''}
                />
              </label>
            ) : null}

            <div className={styles.toggleRow}>
              <label className={styles.toggle}>
                <input
                  checked={draft.collapseByDefault}
                  onChange={(event) => {
                    updateDraft({ collapseByDefault: event.target.checked });
                  }}
                  type="checkbox"
                />
                <span>Collapse by default</span>
              </label>

              <label className={styles.toggle}>
                <input
                  checked={draft.disableAutoNumbering}
                  onChange={(event) => {
                    updateDraft({ disableAutoNumbering: event.target.checked });
                  }}
                  type="checkbox"
                />
                <span>Disable auto numbering</span>
              </label>
            </div>
          </div>
        ) : null}
      </div>

      <div className={styles.editorSurface}>
        {draft.editorType === 'TEXT' ? (
          <TextUnitEditor
            label="Content text"
            onChange={(text) => {
              updateDraft({ text });
            }}
            placeholder="Write lesson content…"
            value={draft.text ?? draft.originalText}
          />
        ) : draft.editorType === 'EQUATION' ? (
          <EquationEditor
            onChange={(latex) => {
              updateDraft({ latex });
            }}
            value={draft.latex}
          />
        ) : draft.editorType === 'IMAGE' ? (
          <ImageUnitEditor
            altText={draft.altText}
            dynamicImageUrl={draft.dynamicImageUrl}
            imageUrl={draft.imageUrl}
            onChange={updateDraft}
            onWaitChange={setImageWait}
            validationError={draft.validationError}
          />
        ) : draft.editorType === 'TABLE' ? (
          <TableUnitEditor
            caption={draft.caption}
            onChange={updateDraft}
            tableDensity={draft.tableDensity}
            tableHeaderRow={draft.tableHeaderRow}
            tableHtml={draft.tableHtml}
            tableStickyHeader={draft.tableStickyHeader}
            tableStyle={draft.tableStyle}
          />
        ) : draft.editorType === 'IFRAME' ? (
          <IframeUnitEditor
            iframeHeight={draft.iframeHeight}
            iframeSource={draft.iframeSource}
            iframeWidth={draft.iframeWidth}
            onChange={updateDraft}
          />
        ) : (
          <VideoUnitEditor
            onChange={updateDraft}
            videoId={draft.videoId}
          />
        )}
      </div>

    </article>
  );
}
